import type { CollectionModelFieldFileTool } from '@ikegps/core-shared';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { FieldInfo } from '../../field.info/field.info';
import type { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

export const CSS = {
	file: 'c-FileTool__file',
	form: 'c-FileTool__form',
	container: 'c-FileTool__container',
	active: 'c-Filetool__active',
};

export const Styles = {
	[CSS.file]: {
		display: 'none',
	},
	[CSS.form]: {
		width: '100%',
		height: '100%',
	},
	[CSS.container]: {
		display: 'flex',
		'justify-content': 'center',
		'align-items': 'center',
		border: '1px solid ' + DefaultTheme.font.color.disabled,
		'&:hover': {
			border: '1px solid ' + DefaultTheme.color.accent,
			cursor: 'pointer',
		},
	},
	[CSS.active]: {
		border: '1px solid ' + DefaultTheme.color.accent,
	},
};

addStyles(Styles);

export const FileToolFieldView: CollectionFieldView<CollectionModelFieldFileTool> = {
	viewInline(field: CollectionModelFieldFileTool): MithrilVNode | string {
		return field.displayValue;
	},

	hasDetail(field: CollectionModelFieldFileTool) {
		return !!field.displayValue;
	},

	viewDetail(field: CollectionModelFieldFileTool): MithrilVNode {
		if (!FileToolFieldView.hasDetail(field)) {
			return null;
		}

		const result = [];
		result.push(FieldInfo.viewStat(T.FIELD_FILE_TOOL_FILE_SIZE, formatBytes(field.stateValue.size)));
		Object.keys(field.stateValue.metadata).forEach((key) => {
			result.push(FieldInfo.viewStat(key as T, field.stateValue.metadata[key]));
		});
		return m.fragment({}, result);
	},

	viewEdit(field: CollectionModelFieldFileTool, ctrl: CollectionEditCtrl): MithrilVNode {
		if (field.displayValue) {
			return m('div', [field.displayValue]);
		}

		if (ctrl.fieldsInLoadingState.length > 0) {
			return m('form', { className: CSS.form }, [
				m(
					'div',
					{
						className: CSS.container,
						disabled: true,
					},
					[Translate(T.DRAG_FILE)]
				),
			]);
		}

		return m('form', { className: CSS.form }, [
			m('input', { className: CSS.file, type: 'file', id: field.id, onchange: (e) => ctrl.onFile(e, field) }, ''),
			m(
				'div',
				{
					id: `drag-area-${field.id}`,
					className: CSS.container,
					onclick: (e) => ctrl.openFileDialog(e, field),
					ondragover: (e) => ctrl.onDragOver(e, field),
					ondragleave: (e) => ctrl.onDragLeave(e, field),
					ondragend: (e) => ctrl.onDragEnd(e, field),
					ondrop: (e) => ctrl.update(e, field),
				},
				[Translate(T.DRAG_FILE)]
			),
		]);
	},

	hasClear(field: CollectionModelFieldFileTool) {
		return !!field.displayValue;
	},

	hasFile(field: CollectionModelFieldFileTool) {
		return !!field.displayValue;
	},
};

function formatBytes(bytes, decimals = 2) {
	if (bytes === 0) {
		return '0 Bytes';
	}

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
